import { type ReactNode, useEffect, useState } from "react";

import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";

import { useAppDispatch } from "app/hooks";
import { useAppSelector } from "app/hooks";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import AssignedTagsList from "components/assigned-tags";
import { TagTypes } from "constants/tags";
import { selectCurrentUser } from "features/auth/auth-slice";
import {
  useAddAssignedTagMutation,
  useRemoveAssignedTagMutation
} from "services/assigned-tags";
import { AssignedTag } from "services/types/assigned-tag.type";
import { Tag } from "services/types/tags.type";

type AppointmentTagsProps = {
  dotsSize?: string;
  appointmentId: number;
  showDotsOnly?: boolean;
  containerSx?: SxProps;
  readOnly?: boolean;
  tags?: AssignedTag[];
  beforeContent?: ReactNode;
  onTagChange?: (tags: AssignedTag[]) => void;
  sx?: SxProps;
};

export default function AppointmentTags({
  dotsSize,
  appointmentId,
  containerSx = {},
  readOnly = false,
  showDotsOnly = false,
  tags = [],
  beforeContent,
  onTagChange,
  sx = {}
}: AppointmentTagsProps) {
  const dispatch = useAppDispatch();

  const [addTag] = useAddAssignedTagMutation();
  const [removeTag] = useRemoveAssignedTagMutation();
  const [appointmentTags, setAppointmentTags] = useState<AssignedTag[]>(tags);
  const currentUser = useAppSelector(selectCurrentUser);

  useEffect(() => {
    if (appointmentId) {
      setAppointmentTags(tags);
    } else {
      setAppointmentTags([]);
    }
  }, [tags, appointmentId]);

  const handleAdd = (tag_id: number, tag: Tag) => {
    addTag({
      tag: tag_id,
      tag_type: TagTypes.APPOINTMENT,
      object_id: appointmentId
    }).then((response) => {
      if ("data" in response) {
        setAppointmentTags((prev) => {
          const newTags = [
            ...prev,
            {
              ...response.data,
              tag,
              created_by: currentUser as any,
              updated_by: currentUser as any
            }
          ];
          onTagChange?.(newTags);

          return newTags;
        });
        dispatch(setCustomMessage("Tag added"));
        dispatch(setOpen(1500));
      }
    });
  };

  const handleUpdate = (updatedTag: AssignedTag) => {
    setAppointmentTags((prev) => {
      const newTags = prev.map((assignedTag) =>
        assignedTag.id === updatedTag.id ? updatedTag : assignedTag
      );
      onTagChange?.(newTags);
      return newTags;
    });
  };

  const handleDelete = (tag: number) => {
    removeTag({ tag, tag_type: TagTypes.APPOINTMENT }).then((response) => {
      if ("data" in response) {
        setAppointmentTags((prev) => {
          const newTags = prev.filter((assignedTag) => assignedTag.id !== tag);
          onTagChange?.(newTags);

          return newTags;
        });
        dispatch(setCustomMessage("Tag removed"));
        dispatch(setOpen(1500));
      }
    });
  };

  return (
    <Grid container sx={{ pt: 0, pb: 1, px: 2, ...containerSx }} spacing={0}>
      <Grid item xs={12}>
        <Stack rowGap={0.5}>
          {beforeContent}
          <AssignedTagsList
            sx={{ mb: -1, ...sx }}
            showDotsOnly={showDotsOnly}
            dotsSize={dotsSize}
            tagType={TagTypes.APPOINTMENT}
            tags={appointmentTags}
            onAdd={!readOnly ? handleAdd : undefined}
            onDelete={!readOnly ? handleDelete : undefined}
            onUpdate={handleUpdate}
          />
        </Stack>
      </Grid>
    </Grid>
  );
}
